import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatCurrency } from '../../lib/utils';
import { 
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Target
} from 'lucide-react';

const ProjectStats = ({ projects }) => {
  // Helper function to get project status
  const getProjectStatus = (project) => {
    const paidAmount = project.totalPaidAmount || 0;
    const totalInstallmentAmount = project.totalPaymentAmount || 0;
    
    if (totalInstallmentAmount === 0) {
      return 'pending';
    }
    
    if (paidAmount >= totalInstallmentAmount) {
      return 'completed';
    }
    
    if (paidAmount > 0) {
      return 'in-progress';
    }
    
    return 'pending';
  };

  // คำนวณสถิติ
  const totalProjects = projects.length;
  const completedProjects = projects.filter(p => {
    const paidAmount = p.totalPaidAmount || 0;
    const totalAmount = p.totalPaymentAmount || 0;
    return totalAmount > 0 && paidAmount >= totalAmount;
  }).length;
  
  const inProgressProjects = projects.filter(p => {
    const paidAmount = p.totalPaidAmount || 0;
    const totalAmount = p.totalPaymentAmount || 0;
    return totalAmount > 0 && paidAmount > 0 && paidAmount < totalAmount;
  }).length;
  
  const pendingProjects = projects.filter(p => {
    const paidAmount = p.totalPaidAmount || 0;
    const totalAmount = p.totalPaymentAmount || 0;
    return totalAmount === 0 || paidAmount === 0;
  }).length;

  // คำนวณข้อมูลทางการเงิน
  const totalRevenue = projects.reduce((sum, p) => sum + (p.project_price || 0), 0);
  const totalPaid = projects.reduce((sum, p) => sum + (p.totalPaidAmount || 0), 0);
  const totalOutstanding = totalRevenue - totalPaid;
  
  // คำนวณกำไรขาดทุน
  const totalActualReceived = projects.reduce((sum, p) => sum + (p.actual_received || 0), 0);
  const totalCompanyFee = projects.reduce((sum, p) => sum + (p.total_company_fee_amount || 0), 0);
  const totalDevPayments = projects.reduce((sum, p) => {
    return sum + (p.developer_payments?.reduce((s, dev) => s + (dev.paid ? Number(dev.amount || 0) : 0), 0) || 0);
  }, 0);
  const totalOtherExpenses = projects.reduce((sum, p) => {
    return sum + (p.other_expenses?.reduce((s, expense) => s + (expense.paid ? Number(expense.amount || 0) : 0), 0) || 0);
  }, 0);
  
  const totalExpenses = totalDevPayments + totalOtherExpenses;
  const netProfit = totalActualReceived - totalExpenses;
  
  // คำนวณสถิติทีม
  const totalDevelopers = projects.reduce((sum, p) => sum + (p.developer_payments?.length || 0), 0);
  const totalInstallments = projects.reduce((sum, p) => sum + (p.totalInstallmentsCount || 0), 0);
  const paidInstallments = projects.reduce((sum, p) => sum + (p.paidInstallmentsCount || 0), 0);
  
  // คำนวณเปอร์เซ็นต์
  const paymentProgress = totalRevenue > 0 ? Math.round((totalPaid / totalRevenue) * 100) : 0;
  const installmentProgress = totalInstallments > 0 ? Math.round((paidInstallments / totalInstallments) * 100) : 0;
  const completionRate = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0;
  
  // คำนวณสถิติเพิ่มเติม
  const overdueProjects = projects.filter(p => {
    const endDate = p.end_date ? new Date(p.end_date) : null;
    const today = new Date();
    const status = getProjectStatus(p);
    return endDate && endDate < today && status !== 'completed';
  }).length;
  
  const thisMonthProjects = projects.filter(p => {
    const createdDate = new Date(p.created_at);
    const today = new Date();
    const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return createdDate >= thisMonth;
  }).length;

  return (
    <div className="space-y-4 mb-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Projects */}
        <Card className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <FileText className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium">โปรเจคทั้งหมด</span>
              </div>
              <Badge variant="secondary" className="bg-blue-200 text-blue-800">
                {completionRate}% เสร็จ
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-700 mb-2">{totalProjects}</div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-1">
                  <CheckCircle className="h-3 w-3 text-green-500" />
                  <span className="text-gray-600">เสร็จสิ้น</span>
                </div>
                <span className="font-medium text-green-600">{completedProjects}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3 text-yellow-500" />
                  <span className="text-gray-600">กำลังทำ</span>
                </div>
                <span className="font-medium text-yellow-600">{inProgressProjects}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-1">
                  <XCircle className="h-3 w-3 text-gray-500" />
                  <span className="text-gray-600">รอดำเนินการ</span>
                </div>
                <span className="font-medium text-gray-600">{pendingProjects}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Overview */}
        <Card className="relative overflow-hidden bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-green-500 rounded-lg">
                  <DollarSign className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium">รายได้รวม</span>
              </div>
              <Badge variant="secondary" className="bg-green-200 text-green-800">
                {paymentProgress}% ชำระ
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-700 mb-2">{formatCurrency(totalRevenue)}</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">ชำระแล้ว</span>
                <span className="font-medium text-green-600">{formatCurrency(totalPaid)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">ค้างชำระ</span>
                <span className="font-medium text-orange-600">{formatCurrency(totalOutstanding)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${paymentProgress}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profit Analysis */}
        <Card className="relative overflow-hidden bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-purple-500 rounded-lg">
                  {netProfit >= 0 ? <TrendingUp className="h-4 w-4 text-white" /> : <TrendingDown className="h-4 w-4 text-white" />}
                </div>
                <span className="font-medium">กำไรสุทธิ</span>
              </div>
              <Badge variant={netProfit >= 0 ? "default" : "destructive"} className={netProfit >= 0 ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}>
                {netProfit >= 0 ? "กำไร" : "ขาดทุน"}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold mb-2 ${netProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(netProfit)}
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">รับจริง</span>
                <span className="font-medium text-blue-600">{formatCurrency(totalActualReceived)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">ค่าใช้จ่าย</span>
                <span className="font-medium text-red-600">{formatCurrency(totalExpenses)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">ค่าบริการ</span>
                <span className="font-medium text-purple-600">{formatCurrency(totalCompanyFee)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team & Performance */}
        <Card className="relative overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <span className="font-medium">ทีม & ประสิทธิภาพ</span>
              </div>
              <Badge variant="secondary" className="bg-orange-200 text-orange-800">
                {installmentProgress}% งวด
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-700 mb-2">{totalDevelopers}</div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Developer</span>
                <span className="font-medium text-orange-600">{totalDevelopers} คน</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">งวดชำระ</span>
                <span className="font-medium text-blue-600">{paidInstallments}/{totalInstallments}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">โปรเจคใหม่</span>
                <span className="font-medium text-green-600">{thisMonthProjects} นี้</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Overdue Projects Alert */}
        {overdueProjects > 0 && (
          <Card className="bg-gradient-to-r from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-500 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-red-800">โปรเจคเกินกำหนด</div>
                  <div className="text-2xl font-bold text-red-700">{overdueProjects}</div>
                  <div className="text-xs text-red-600">โปรเจคที่ควรเสร็จแล้ว</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* This Month Performance */}
        <Card className="bg-gradient-to-r from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-500 rounded-lg">
                <Calendar className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-semibold text-indigo-800">โปรเจคใหม่เดือนนี้</div>
                <div className="text-2xl font-bold text-indigo-700">{thisMonthProjects}</div>
                <div className="text-xs text-indigo-600">โปรเจคที่เพิ่มใหม่</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Target */}
        <Card className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-semibold text-emerald-800">อัตราเสร็จสิ้น</div>
                <div className="text-2xl font-bold text-emerald-700">{completionRate}%</div>
                <div className="text-xs text-emerald-600">โปรเจคที่เสร็จสิ้น</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectStats;
